import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogComponent } from '@app/components';
import { RequestCustomBenefitTypeForm } from '@app/modules/forms/payroll/components';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AuthService, NotifyService } from '@app/services';
import { CompanyBenefit } from '@models/payroll/company/benefit.model';
import { CustomBenefitType } from '@models/payroll/custom-benefit-type.model';
import { BenefitTable } from '@payroll/services';

@Component({
    templateUrl: './benefit-index.template.html',
})
export class BenefitIndexView {
    @ViewChild('deleteConfirmationDialog') deleteConfirmationDialog!: DialogComponent;

    isLoading = false;
    table: BenefitTable;
    currentBenefit?: CompanyBenefit;
    currentlyOpenDialog?: 'REQUEST_CUSTOM_BENEFIT' | undefined;

    constructor(
        public router: Router,
        public route: ActivatedRoute,
        public auth: AuthService,
        private notify: NotifyService,
        public payrollModuleMenuService: PayrollModuleMenuService
    ) {
        this.table = new BenefitTable(this.auth);

        this.table.links = this.route.snapshot.data.isPayrollPending ? false : true;
        this.table.showMenuColumnOptions = this.route.snapshot.data.isPayrollPending ? false : true;
    }

    /**
     * The request custom benefit type form is an mvp
     * Keeping it simple only the name and description fields are sent to the backend
     * So all additional fields are being concatenated and passed via the description
     */
    onCreate(customBenefitType: CustomBenefitType, form: RequestCustomBenefitTypeForm): void {
        this.isLoading = true;

        customBenefitType.description = '';

        if (form.selectedT4Boxes?.length) {
            customBenefitType.description += `T4 Boxes
${form.selectedT4Boxes.map((t4Box) => 'Box ' + t4Box)}\n`;
        }

        if (form.selectedRl1Boxes?.length) {
            customBenefitType.description += `\nRL-1 Boxes
${form.selectedRl1Boxes.map((t4Box) => 'Box ' + t4Box)}\n`;
        }

        customBenefitType.description += `
Employee Contribution

Federal
Pre-tax: ${customBenefitType.employeePretax ? 'YES' : 'NO'}

Provincial
Vacation Pay: ${customBenefitType.employeeVacationPay ? 'YES' : 'NO'}

ROE Reporting
Insurable Hours: ${customBenefitType.employeeInsurableHours ? 'YES' : 'NO'}
Insurable Earnings: ${customBenefitType.employeeInsurableEarnings ? 'YES' : 'NO'}


Employer Contribution

Federal
Taxable: ${customBenefitType.employerTaxable ? 'YES' : 'NO'}
Pensionable: ${customBenefitType.employerPensionable ? 'YES' : 'NO'}
Insurable: ${customBenefitType.employerInsurable ? 'YES' : 'NO'}

Provincial
ON Employer Tax: ${customBenefitType.employerTax ? 'YES' : 'NO'}
Assessable for Workers' Comp: ${customBenefitType.employerAssessable ? 'YES' : 'NO'}
`;

        customBenefitType
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.notify.success('payroll.benefits.requestSubmitted');
                form.reset();
                form.resetSelectedT4();
                this.isLoading = false;
                this.currentlyOpenDialog = undefined;
            })
            .catch((err) => {
                this.notify.error(err.message);
                this.isLoading = false;
            });
    }

    onCancel(form: RequestCustomBenefitTypeForm): void {
        form.reset();
        form.resetSelectedT4();
        this.currentlyOpenDialog = undefined;
    }

    menuAction(event: MenuColumnEvent<CompanyBenefit>): void {
        const { menuItem, data: benefit } = event;

        switch (menuItem.event) {
            case 'edit':
                this.router.navigate([benefit?.id, 'edit'], { relativeTo: this.route });
                break;
            case 'delete':
                this.currentBenefit = benefit;
                this.deleteConfirmationDialog.open = true;
                break;
        }
    }

    async onDelete(benefit?: CompanyBenefit): Promise<void> {
        if (!benefit) {
            return;
        }

        try {
            this.isLoading = true;

            await benefit.param('company', this.auth.company.id).delete();

            this.notify.success('success');
            this.table.reload();
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }
}
